import { useEffect, useState } from "react";
import type { Field, Refs, Row } from "../types";
import { IconClose } from "../icons";
import { MENU_PERMISSOES, TODAS_PERMISSOES, PERMISSOES_COLABORADOR } from "../config";
import { rotuloRef } from "../format";
import SkillsInput from "./SkillsInput";
import { ehDoOperacional } from "../skills-catalogo";
import { ordenarPorNome } from "../ordem";

interface Props {
  title: string;
  fields: Field[];
  initial?: Row | null;
  refs: Refs; // listas para selects com ref (colaboradores / obras)
  /** Aba espelhada do RH/DP: campos com `origemRh` ficam só de leitura. */
  bloquearOrigemRh?: boolean;
  /** Registro inteiro só de leitura (não há o que salvar) — esconde "Salvar". */
  somenteLeitura?: boolean;
  /**
   * Envia só os campos que a pessoa mudou (edição). Sem mudança nenhuma,
   * "Salvar" só fecha — nada vai ao servidor. Ver `salvarSoAlterados` na aba.
   */
  enviarSoAlterados?: boolean;
  onCancel: () => void;
  onSubmit: (data: Row) => Promise<void>;
}

/** Mesmo valor para o formulário? (o select devolve "5" onde o registro tinha 5) */
function mesmoValor(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) || Array.isArray(b)) return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
  return String(a ?? "") === String(b ?? "");
}

/** Modal de cadastro/edição gerado a partir da definição de campos. */
export default function RecordForm({
  title,
  fields,
  initial,
  refs,
  bloquearOrigemRh,
  somenteLeitura,
  enviarSoAlterados,
  onCancel,
  onSubmit,
}: Props) {
  const [form, setForm] = useState<Row>({});
  /** Como o formulário abriu — é contra isto que se sabe o que mudou. */
  const [inicial, setInicial] = useState<Row>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Campos calculados no banco (ex.: saldo, data_limite, situação) não são
  // editáveis; os `soLeitura` também não — o valor vem pronto de outro sistema
  // (ex.: "Em uso", que sai das OS abertas no Relatórios).
  const editable = fields.filter((f) => !f.computed && !f.soLeitura);

  // Aba espelhada do RH/DP: o registro é só leitura, MENOS os campos que este
  // sistema mantém (hoje: a marcação de competências). Havendo algum, o
  // formulário continua tendo o que salvar.
  const temCampoLocal = editable.some((f) => !f.origemRh);
  const podeSalvar = !somenteLeitura || temCampoLocal;

  useEffect(() => {
    const base: Row = {};
    for (const f of editable) {
      if (f.type === "permissoes") {
        // Array de chaves de aba. Novo usuário => padrão colaborador; edição de
        // usuário legado (permissoes null) => tudo marcado (sem restrição).
        const val = initial ? initial[f.name] : undefined;
        base[f.name] = Array.isArray(val)
          ? [...val]
          : initial
            ? [...TODAS_PERMISSOES]
            : [...PERMISSOES_COLABORADOR];
        continue;
      }
      let v = initial ? initial[f.name] : "";
      if (f.type === "date" && v) v = String(v).slice(0, 10); // YYYY-MM-DD
      base[f.name] = v ?? "";
    }
    setForm(base);
    setInicial(base);
    setError(null);
  }, [initial, fields]);

  const set = (name: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    // Campo travado só se mostra: nunca vai no envio (quem muda é outra tela).
    const travados = new Set(fields.filter((f) => f.travado).map((f) => f.name));
    const dados: Row = {};
    for (const [k, v] of Object.entries(form)) {
      if (travados.has(k)) continue;
      if (enviarSoAlterados && mesmoValor(v, inicial[k])) continue;
      dados[k] = v;
    }
    // Nada mudou: salvar é o mesmo que fechar (e evita um PUT vazio).
    if (enviarSoAlterados && Object.keys(dados).length === 0) {
      onCancel();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(dados);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button type="button" className="icon-btn" onClick={onCancel}>
            <IconClose size={18} />
          </button>
        </div>

        {/* O rodapé fica FORA do form (preso embaixo, sem entrar na rolagem):
            o botão Salvar continua enviando este form pelo atributo `form`. */}
        <form id="record-form" className="modal-body" onSubmit={submit}>
          <div className="form-grid">
            {editable.map((f) => {
              // Competências por marcação: também é grade, então usa <div>.
              if (f.type === "skills") {
                const travadoSkills = Boolean(bloquearOrigemRh && f.origemRh);
                // A lista é toda de serviço de campo: só vale para quem é do
                // Operacional (é assim também no cadastro do RH). Setor em
                // branco continua mostrando — aqui o padrão é ser da operação.
                const setor = String(form.setor ?? "").trim();
                if (setor && !ehDoOperacional(setor)) {
                  return (
                    <div key={f.name} className="field field-wide">
                      <span>{f.label}</span>
                      <p className="muted">
                        Competências são do pessoal do <strong>Operacional</strong> — este
                        colaborador é do setor {setor}.
                      </p>
                    </div>
                  );
                }
                return (
                  <div key={f.name} className="field field-wide">
                    <span>{f.label}</span>
                    <SkillsInput
                      value={String(form[f.name] ?? "")}
                      disabled={travadoSkills}
                      onChange={(next) => set(f.name, next)}
                    />
                    {!travadoSkills && (
                      <p className="muted">
                        Marque o que o colaborador faz em campo. A marcação sobe para o cadastro
                        do RH/DP e vale para todo o portal.
                      </p>
                    )}
                  </div>
                );
              }
              // Grade de checkboxes: usa <div> (um <label> englobaria a grade toda).
              if (f.type === "permissoes") {
                return (
                  <div key={f.name} className="field field-wide">
                    <span>
                      {f.label}
                      {f.required && <em className="req"> *</em>}
                    </span>
                    <PermissoesInput
                      value={(form[f.name] as string[]) ?? []}
                      onChange={(next) => set(f.name, next)}
                    />
                  </div>
                );
              }
              // Campo mantido pelo RH/DP (fonte única): exibido, mas não editável.
              const travadoRh = Boolean(bloquearOrigemRh && f.origemRh);
              // Campo que só se mostra (o código de barras do equipamento de
              // ensaio): muda por outra tela, então aqui fica travado e com "—"
              // no lugar do vazio, para não parecer que falta preencher.
              const travado = travadoRh || !!f.travado;
              const valor = f.travado && !form[f.name] ? "—" : form[f.name];
              return (
                <label
                  key={f.name}
                  className={f.type === "textarea" ? "field field-wide" : "field"}
                >
                  <span>
                    {f.label}
                    {f.required && !travado && <em className="req"> *</em>}
                    {travadoRh && <em className="campo-rh"> · RH/DP</em>}
                    {f.travado && <em className="campo-rh"> · só leitura</em>}
                  </span>
                  {renderInput(f, valor, set, refs, travado)}
                </label>
              );
            })}
          </div>
        </form>

        <div className="modal-foot">
          {error && <div className="form-error">{error}</div>}
          <button type="button" className="btn-secondary" onClick={onCancel}>
            {podeSalvar ? "Cancelar" : "Fechar"}
          </button>
          {podeSalvar && (
            <button
              type="submit"
              form="record-form"
              className="btn-primary"
              disabled={saving}
            >
              {saving
                ? "Salvando..."
                : somenteLeitura
                  ? "Salvar competências"
                  : "Salvar"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Grade de checkboxes das abas liberadas (controle de acesso do usuário). */
function PermissoesInput({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const has = (k: string) => value.includes(k);
  const toggle = (k: string) =>
    onChange(has(k) ? value.filter((x) => x !== k) : [...value, k]);

  return (
    <div className="perm-box">
      <div className="perm-actions">
        <button
          type="button"
          className="btn-link"
          onClick={() => onChange([...TODAS_PERMISSOES])}
        >
          Marcar todas
        </button>
        <span className="perm-sep">·</span>
        <button
          type="button"
          className="btn-link"
          onClick={() => onChange([...PERMISSOES_COLABORADOR])}
        >
          Perfil colaborador
        </button>
        <span className="perm-sep">·</span>
        <button type="button" className="btn-link" onClick={() => onChange([])}>
          Limpar
        </button>
      </div>
      <div className="perm-grid">
        {MENU_PERMISSOES.map((m) => (
          <label key={m.key} className="perm-check">
            <input type="checkbox" checked={has(m.key)} onChange={() => toggle(m.key)} />
            <span>{m.label}</span>
          </label>
        ))}
      </div>
      <p className="perm-hint muted">
        O colaborador vê no menu apenas as abas marcadas. Usuários com cargo
        "admin" têm acesso total, independentemente desta seleção.
      </p>
    </div>
  );
}

function renderInput(
  f: Field,
  value: unknown,
  set: (name: string, value: unknown) => void,
  refs: Refs,
  /** Campo mantido por outro sistema (RH/DP): exibido, mas não editável. */
  travado = false,
) {
  const v = (value ?? "") as string | number;
  // Campo travado não é obrigatório aqui (o valor vem pronto da fonte).
  const req = f.required && !travado;

  if (f.ref) {
    // Todo select de cadastro em ordem alfabética pelo MESMO texto que aparece
    // na opção (nome; no veículo, a placa) — é o componente compartilhado,
    // então ordenar aqui vale para todas as abas.
    const list = ordenarPorNome(refs[f.ref], rotuloRef);
    return (
      <select
        value={v}
        required={req}
        disabled={travado}
        onChange={(e) => set(f.name, e.target.value)}
      >
        <option value="">Selecione...</option>
        {list.map((item) => (
          <option key={item.id} value={item.id}>
            {rotuloRef(item)}
          </option>
        ))}
      </select>
    );
  }

  switch (f.type) {
    case "select":
    case "status":
      return (
        <select
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        >
          <option value="">Selecione...</option>
          {f.options?.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    case "textarea":
      return (
        <textarea
          rows={3}
          value={v}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "number":
      return (
        <input
          type="number"
          step="any"
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "currency":
      return (
        <input
          type="number"
          step="0.01"
          min="0"
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "date":
      return (
        <input
          type="date"
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "email":
      return (
        <input
          type="email"
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "password":
      return (
        <input
          type="password"
          value={v}
          required={req}
          disabled={travado}
          placeholder={f.placeholder}
          autoComplete="new-password"
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    default:
      return (
        <input
          type="text"
          value={v}
          required={req}
          disabled={travado}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
  }
}
